/*
  Shared by the homepage sections that slide cards sideways and play TikTok
  videos in place: assets/community-reviews.js and assets/trending-social.js.
  Each section loads this file before its own script.

  - attachRow(): arrows that scroll a row a card at a time and are shown
    only while it overflows.
  - createPlayer(): TikTok's embed player (tiktok.com/player/v1) for a
    poster link, built only when someone asks for the video.
  - claimPlayer() / releasePlayer(): one player on the whole page. Starting
    a video stops the one already playing, in this section or another.
  - stopActive(): stops whichever video is playing, for a dialog that opens
    over the page.
*/
(() => {
  if (window.chellaMediaRows) return;

  /** @type {(() => void) | null} stops the player now on the page */
  let activeStop = null;

  /**
   * @param {object} options
   * @param {HTMLElement} options.track - the scrolling list
   * @param {HTMLElement | null} options.controls - shown only while the track overflows
   * @param {HTMLButtonElement[]} options.arrows
   * @param {(arrow: HTMLElement) => number} options.direction - -1 or 1 for an arrow
   * @param {AbortSignal} options.signal - removes the listeners and observer
   */
  function attachRow({ track, controls, arrows, direction, signal }) {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = track.scrollWidth - track.clientWidth;
      if (controls) controls.hidden = max <= 1;

      for (const arrow of arrows) {
        const atEnd = direction(arrow) < 0 ? track.scrollLeft <= 1 : track.scrollLeft >= max - 1;
        // A focused button that turns disabled drops focus to the page, so
        // hand it to the other arrow first.
        if (atEnd && document.activeElement === arrow) {
          arrows.find((other) => other !== arrow)?.focus();
        }
        arrow.disabled = atEnd;
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    track.addEventListener('scroll', schedule, { passive: true, signal });
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(track);
    update();

    for (const arrow of arrows) {
      arrow.addEventListener(
        'click',
        () => {
          const card = track.firstElementChild;
          const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
          track.scrollBy({ left: step * direction(arrow), behavior: 'smooth' });
        },
        { signal }
      );
    }

    signal.addEventListener(
      'abort',
      () => {
        resizeObserver.disconnect();
        cancelAnimationFrame(frame);
      },
      { once: true }
    );
  }

  /**
   * @param {HTMLElement} poster - carries the player URL in data-embed
   * @param {string} className
   * @param {string} marker - attribute the section finds its player by
   * @returns {HTMLIFrameElement}
   */
  function createPlayer(poster, className, marker) {
    const frame = document.createElement('iframe');
    frame.className = className;
    frame.src = poster.dataset.embed || '';
    frame.title = poster.getAttribute('aria-label') || 'TikTok video';
    frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
    frame.allowFullscreen = true;
    frame.setAttribute(marker, '');
    return frame;
  }

  /** @param {() => void} stop - stops the caller's player; called if another starts */
  function claimPlayer(stop) {
    if (activeStop && activeStop !== stop) activeStop();
    activeStop = stop;
  }

  /** @param {() => void} stop - the function passed to claimPlayer */
  function releasePlayer(stop) {
    if (activeStop === stop) activeStop = null;
  }

  function stopActive() {
    if (activeStop) activeStop();
  }

  window.chellaMediaRows = { attachRow, createPlayer, claimPlayer, releasePlayer, stopActive };
})();
